(() => {
  const TRACKING_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'fbclid',
    'gclid',
    'mc_cid',
    'mc_eid',
    '_pos'
  ];

  const RESERVED_PARAMS = new Set([
    'view',
    'page',
    'event',
    'shop',
    'collection',
    'filter_id',
    'country',
    'locale',
    'currency',
    'cache',
    'ncp',
    'page_type',
    'sort_by',
    'q',
    'limit',
    'form_id',
    'market_id',
    'vendor',
    'cid',
    'did',
    'operation',
    'rate',
    'adjustment',
    ...TRACKING_PARAMS
  ]);

  function shopRoot() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root.slice(0, -1) : root;
  }

  function parsePastedUrl(rawUrl) {
    if (!rawUrl) return null;

    try {
      const parsed = new URL(rawUrl.trim(), window.location.origin);
      const path = parsed.pathname.replace(/\/+$/, '') || '/';
      const isCollection = /\/collections\/.+/.test(path);
      const isSearch = path === '/search' || path.endsWith('/search');
      if (!isCollection && !isSearch) return null;

      if (parsed.hash && parsed.hash.includes('filter.')) {
        const hash = parsed.hash.replace(/^#\/?/, '').replace(/^\?/, '');
        new URLSearchParams(hash).forEach((value, key) => {
          if (!parsed.searchParams.has(key)) parsed.searchParams.append(key, value);
        });
      }

      TRACKING_PARAMS.forEach((key) => parsed.searchParams.delete(key));
      parsed.searchParams.delete('page');
      parsed.searchParams.delete('view');

      return { path, searchParams: parsed.searchParams, isCollection, isSearch };
    } catch (error) {
      return null;
    }
  }

  function globoFormParams(searchParams) {
    const form = [];
    searchParams.forEach((value, key) => {
      if (!value || RESERVED_PARAMS.has(key) || key.startsWith('filter.')) return;
      form.push([key, value]);
    });
    return form;
  }

  function parseGloboConfig(html) {
    const preload =
      html.match(/id=["']gf-filter-preload["'][^>]*href=["']([^"']+)["']/) ||
      html.match(/id=["']gf-filter-preload["'][^>]*href=["']([^"']+)["']/) ||
      html.match(/href=["']([^"']*\/api\/apiFilter\?[^"']+)["']/);

    if (preload) {
      try {
        const preloadUrl = new URL(preload[1].replace(/&amp;/g, '&'), window.location.origin);
        if (preloadUrl.searchParams.get('filter_id') && preloadUrl.searchParams.get('collection')) {
          return {
            apiFilter: `${preloadUrl.origin}/api/apiFilter`,
            filterId: preloadUrl.searchParams.get('filter_id'),
            collectionId: preloadUrl.searchParams.get('collection'),
            shop: preloadUrl.searchParams.get('shop'),
            country: preloadUrl.searchParams.get('country') || 'US',
            ncp: preloadUrl.searchParams.get('ncp'),
            sortBy: preloadUrl.searchParams.get('sort_by')
          };
        }
      } catch (error) {
        // Try the embed config next.
      }
    }

    const apiHost = html.match(/https:\/\/filter-[a-z0-9-]+\.globo\.io/);
    const filterId = html.match(/filter:\s*\{\s*id:\s*(\d+)/);
    const collectionId = html.match(/collection:\s*\{\s*id:\s*(\d+)/);
    const shop =
      html.match(/shop=([a-z0-9-]+\.myshopify\.com)/) ||
      html.match(/domain:\s*"(?:https:\/\/)?([a-z0-9-]+\.myshopify\.com)"/);
    const country = html.match(/country=([A-Z]{2})/) || html.match(/country_code:\s*"([A-Z]{2})"/);
    const ncp = html.match(/ncp=(\d+)/) || html.match(/products_count:\s*(\d+)/);

    if (!filterId || !collectionId || !shop) return null;

    return {
      apiFilter: `${apiHost ? apiHost[0] : 'https://filter-x4.globo.io'}/api/apiFilter`,
      filterId: filterId[1],
      collectionId: collectionId[1],
      shop: shop[1],
      country: country ? country[1] : 'US',
      ncp: ncp ? ncp[1] : null,
      sortBy: 'manual'
    };
  }

  async function loadGloboConfig(path) {
    const response = await fetch(path, { credentials: 'same-origin' });
    if (!response.ok) return null;
    return parseGloboConfig(await response.text());
  }

  function buildGloboApiUrl(config, parsed, limit) {
    const params = new URLSearchParams();
    params.set('filter_id', config.filterId);
    params.set('collection', config.collectionId);
    params.set('shop', config.shop);
    params.set('country', config.country);
    params.set('page_type', parsed.isSearch ? 'search' : 'collection');
    params.set('event', 'init');
    params.set('limit', String(limit));

    if (config.ncp) params.set('ncp', config.ncp);

    const sortBy = parsed.searchParams.get('sort_by') || config.sortBy;
    if (sortBy) params.set('sort_by', sortBy);

    const query = parsed.searchParams.get('q');
    if (query) {
      params.set('q', query);
      params.set('operation', 'filter');
    }

    globoFormParams(parsed.searchParams).forEach(([key, value]) => {
      params.append(`form[${key}][]`, value);
    });

    return `${config.apiFilter}?${params.toString()}`;
  }

  async function fetchGloboProducts(parsed, limit) {
    const config = await loadGloboConfig(parsed.path);
    if (!config?.filterId || !config.collectionId || !config.shop) return [];

    const response = await fetch(buildGloboApiUrl(config, parsed, limit), {
      method: 'GET',
      credentials: 'omit',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) return [];

    const data = await response.json();
    return Array.isArray(data.products) ? data.products : [];
  }

  async function fetchShopifyViewCards(parsed, limit) {
    const params = new URLSearchParams(parsed.searchParams);
    params.set('view', 'featured-url-products');
    const query = params.toString();
    const response = await fetch(query ? `${parsed.path}?${query}` : `${parsed.path}?view=featured-url-products`, {
      credentials: 'same-origin'
    });
    if (!response.ok) return [];

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(doc.querySelectorAll('[data-featured-url-product]')).slice(0, limit);
  }

  async function fetchProductCard(handle) {
    const response = await fetch(`${shopRoot()}/products/${handle}?view=featured-url-card`, {
      credentials: 'same-origin'
    });
    if (!response.ok) return null;

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('[data-featured-url-product]');
  }

  async function fetchProductCards(handles) {
    const cards = [];
    const batchSize = 8;

    for (let i = 0; i < handles.length; i += batchSize) {
      const batch = handles.slice(i, i + batchSize);
      const results = await Promise.all(batch.map((handle) => fetchProductCard(handle)));
      results.forEach((card) => {
        if (card) cards.push(card);
      });
    }

    return cards;
  }

  function refreshCarousel(list) {
    const carousel = list.closest('carousel-slider');
    if (!carousel) return;

    carousel.slides = carousel.querySelectorAll('.slider__item');
    if (carousel.slides.length < 2) {
      carousel.setAttribute('inactive', '');
      return;
    }

    carousel.removeAttribute('inactive');
    if (carousel.slider && typeof carousel.handleBreakpointChange === 'function') {
      carousel.handleBreakpointChange();
    }
  }

  function renderCards(list, items) {
    if (!items.length) return false;

    const isCarousel = Boolean(list.closest('carousel-slider'));
    const fragment = document.createDocumentFragment();

    items.forEach((item) => {
      const li = document.importNode(item, true);
      li.classList.toggle('slider__item', isCarousel);
      fragment.appendChild(li);
    });

    list.replaceChildren(fragment);
    refreshCarousel(list);
    return true;
  }

  async function loadCollectionUrlProducts(list) {
    if (list.dataset.featuredCollectionUrlLoaded === 'true') return;

    const parsed = parsePastedUrl(list.getAttribute('data-featured-collection-url'));
    if (!parsed) return;

    const limit = Number(list.getAttribute('data-products-to-show')) || 24;
    list.dataset.featuredCollectionUrlLoaded = 'true';
    list.setAttribute('aria-busy', 'true');

    try {
      let rendered = false;

      try {
        const products = await fetchGloboProducts(parsed, limit);
        const handles = products
          .map((product) => product.handle)
          .filter(Boolean)
          .slice(0, limit);

        if (handles.length) {
          const cards = await fetchProductCards(handles);
          rendered = renderCards(list, cards);
        }
      } catch (globoError) {
        rendered = false;
      }

      if (!rendered) {
        try {
          const cards = await fetchShopifyViewCards(parsed, limit);
          rendered = renderCards(list, cards);
        } catch (shopifyError) {
          rendered = false;
        }
      }

      if (!rendered) {
        list.dataset.featuredCollectionUrlLoaded = 'false';
      }
    } catch (error) {
      list.dataset.featuredCollectionUrlLoaded = 'false';
    } finally {
      list.removeAttribute('aria-busy');
    }
  }

  function initFeaturedCollectionUrls(scope) {
    const lists = [];
    if (scope && scope.nodeType === 1 && scope.matches('[data-featured-collection-url]')) {
      lists.push(scope);
    }
    if (scope && scope.querySelectorAll) {
      scope.querySelectorAll('[data-featured-collection-url]').forEach((list) => lists.push(list));
    }
    lists.forEach(loadCollectionUrlProducts);
  }

  window.initFeaturedCollectionUrls = initFeaturedCollectionUrls;

  if (!window.__featuredCollectionUrlListeners) {
    window.__featuredCollectionUrlListeners = true;
    document.addEventListener('shopify:section:load', (event) => {
      initFeaturedCollectionUrls(event.target);
    });
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => initFeaturedCollectionUrls(document));
    }
  }

  initFeaturedCollectionUrls(document);
})();
